import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { listModels, deleteModel, reorderModels, upsertModel } from '@/src/api/model'
import type { AiModel } from '@/src/types/ai.zod'
import { SimpleCopyCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { DotsSixVerticalIcon, OpenAiLogoIcon, GoogleLogoIcon, MetaLogoIcon } from '@phosphor-icons/react'
import { EditAction, DeleteAction } from '@/src/components/manage/actions'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from '@/src/i18n/client'
import { toast } from 'sonner'
import { TableCell } from '@/src/components/ui/table'
import { DataTable, ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { useState, useEffect } from 'react'

export const Route = createFileRoute('/_manage/manage/admin/models/')({
  component: AdminModelsPage,
})

const getProviderBadgeClasses = (provider?: string) => {
  switch (provider) {
    case 'openai':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
    case 'google':
    case 'gemini':
      return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200'
    case 'groq':
      return 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-800 dark:bg-fuchsia-950 dark:text-fuchsia-200'
    case 'mistral':
      return 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-200'
    case 'anthropic':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
    case 'deepseek':
      return 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200'
    default:
      return 'border-muted-foreground/20 bg-muted/40 text-foreground'
  }
}

const getProviderIcon = (provider?: string) => {
  switch (provider) {
    case 'openai':
      return <OpenAiLogoIcon className="w-4 h-4 inline mr-1" />
    case 'google':
    case 'gemini':
      return <GoogleLogoIcon className="w-4 h-4 inline mr-1" />
    case 'groq':
      return <MetaLogoIcon className="w-4 h-4 inline mr-1" />
    default:
      return null
  }
}

const getProviderName = (provider?: string) => {
  switch (provider) {
    case 'openai':
      return 'OpenAI'
    case 'google':
    case 'gemini':
      return 'Google'
    case 'groq':
      return 'Meta'
    case 'mistral':
      return 'Mistral'
    case 'anthropic':
      return 'Anthropic'
    case 'deepseek':
      return 'Deepseek'
    default:
      return 'Unknown'
  }
}

function SortableRow({ model, columns, actions }: { model: AiModel; columns: ColumnConfig<AiModel>[]; actions?: (model: AiModel) => React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: model.id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors">
      {columns.map((col) => (
        <TableCell
          key={col.key}
          className={col.align === "right" ? "text-right" : undefined}
        >
          {col.key === 'drag' ? (
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              {col.render ? col.render(model) : "-"}
            </div>
          ) : (
            <>{col.render ? col.render(model) : (model[col.key as keyof AiModel] as any) || "-"}</>
          )}
        </TableCell>
      ))}
      {actions && (
        <TableCell className="text-right pr-0">
          {actions(model)}
        </TableCell>
      )}
    </tr>
  )
}

function AdminModelsPage() {
  const { t } = useTranslation('manage')
  const queryClient = useQueryClient()
  const [sortedModels, setSortedModels] = useState<AiModel[]>([])

  const { data: models, isLoading, error } = useQuery({
    queryKey: ['models', 'all'],
    queryFn: () => listModels({ data: { all: true } }),
  })

  // Update sorted models when data changes
  useEffect(() => {
    if (models && Array.isArray(models)) {
      setSortedModels([...models])
    }
  }, [models])

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteModel({ data: { id } }),
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['models'] })
      // Update local state immediately
      setSortedModels((items) => items.filter((item) => item.id !== deletedId))
      toast.success(t('models.modelDeleted'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('models.failedToDeleteModel'))
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderModels({ data: { ids } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] })
    },
    onError: (err: any) => {
      toast.error(err?.message || t('models.failedToReorderModels'))
      // Revert on error
      if (models && Array.isArray(models)) {
        setSortedModels([...models])
      }
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ model, enabled }: { model: AiModel; enabled: boolean }) =>
      upsertModel({
        data: {
          id: model.id,
          name: model.name,
          provider: model.provider,
          model: model.model,
          label: model.label,
          description: model.description,
          enabled,
          isDefault: model.isDefault,
          function: model.function,
          permissions: model.permissions,
          order: model.order,
          thinkingAmount: model.thinkingAmount,
          temperature: model.temperature,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] })
    },
    onError: (err: any) => {
      toast.error(err?.message || t('models.failedToUpdateModel'))
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setSortedModels((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        
        // Update order on server
        const ids = newOrder.map((item) => item.id!)
        reorderMutation.mutate(ids)
        
        return newOrder
      })
    }
  }

  const columns: ColumnConfig<AiModel>[] = [
    {
      key: 'drag',
      label: '',
      width: 'w-12',
      render: () => (
        <DotsSixVerticalIcon className="w-4 h-4 text-muted-foreground" />
      ),
    },
    {
      key: 'name',
      label: t('models.columns.name'),
      render: (model) => (
        <div>
          <div className="font-medium">{model.name}</div>
          <div className="text-xs text-muted-foreground">{model.label}</div>
        </div>
      ),
    },
    {
      key: 'provider',
      label: t('models.columns.provider'),
      render: (model) => (
        <Badge
          variant="outline"
          className={`capitalize ${getProviderBadgeClasses(model.provider)}`}
        >
          {getProviderIcon(model.provider)}
          {getProviderName(model.provider)}
        </Badge>
      ),
    },
    {
      key: 'model',
      label: t('models.columns.model'),
      render: (model) => <SimpleCopyCell value={model.model} />,
    },
    {
      key: 'function',
      label: t('models.columns.function'),
      render: (model) => model.function,
    },
    {
      key: 'permissions',
      label: t('models.columns.permissions'),
      render: (model) => (
        <div className="flex flex-wrap gap-1">
          {model.permissions.slice(0, 2).map((perm) => (
            <Badge key={perm} variant="secondary" className="text-xs">
              {perm}
            </Badge>
          ))}
          {model.permissions.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{model.permissions.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'isDefault',
      label: t('models.columns.default'),
      render: (model) =>
        model.isDefault ? (
          <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200">
            {t('models.default')}
          </Badge>
        ) : null,
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (model) => (
        <EnabledAction
          enabled={!!model.enabled}
          ariaLabel={model.enabled ? t('models.enabled') : t('models.disabled')}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ model, enabled })
          }}
        />
      ),
    },
  ]

  const renderActions = (model: AiModel) => (
    <>
      <EditAction
        to="/manage/admin/models/$id"
        params={{ id: model.id! }}
      />
      <DeleteAction
        itemName={model.name!}
        itemType="model"
        onDelete={async () => {
          await deleteMutation.mutateAsync(model.id!)
        }}
        isPending={deleteMutation.isPending}
      />
    </>
  )

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <Link to="/manage/admin/models/$id" params={{ id: 'new' }}>
            <Button size="lg">{t('models.addModel')}</Button>
          </Link>
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="mt-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={(sortedModels.length > 0 ? sortedModels : ((models as any) ?? [])).map((m: AiModel) => m.id!)}
            strategy={verticalListSortingStrategy}
          >
            <DataTable
              data={sortedModels.length > 0 ? sortedModels : ((models as any) ?? [])}
              isLoading={isLoading}
              error={error instanceof Error ? error : error ? String(error) : null}
              columns={columns}
              actions={renderActions}
              renderRow={(model) => (
                <SortableRow
                  key={model.id}
                  model={model}
                  columns={columns}
                  actions={renderActions}
                />
              )}
              emptyMessage={t('models.emptyMessage')}
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
